import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Skeleton,
  Typography,
  Tooltip,
  IconButton,
  Link,
} from '@mui/material';
import { EDIT_STATUS_NOTE_INFO_LINK } from '../Win32Apps/constants';
import Iconify from 'src/components/Iconify';
import { USER_QUERY_KEY } from 'src/constants/query';
import { buildDefaultStatusNote } from 'src/features/appStatus/defaultStatusNote';
import {
  UPDATE_TRUST_STATUS_ICON,
  UPDATE_TRUST_STATUS_TITLE,
} from 'src/features/appStatus/UpdateStatusModal';
import useQueryData from 'src/queries/useQueryData';
import { userEmailSelector } from 'src/selectors/user';
import { UNMATCHED_FILE_STATUS } from 'src/utils/enums/unmatchedFileStatus';
import UnmatchedFileStatusForm from './UnmatchedFileStatusForm';

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const UnmatchedFileStatusModal = ({
  open,
  onClose,
  rows = [],
  noun = 'file',
  count: countProp,
  fileCount,
  fileCountLoading = false,
  summary: summaryProp,
  initialStatus: initialStatusProp,
  filterScope,
  onSave,
  loading,
}) => {
  const count = countProp ?? rows.length;
  const initialStatusRaw =
    initialStatusProp !== undefined
      ? initialStatusProp
      : count === 1
        ? rows[0]?.trustStatus
        : undefined;
  const initialStatus = Object.values(UNMATCHED_FILE_STATUS).includes(initialStatusRaw)
    ? initialStatusRaw
    : undefined;
  // Every save, however it is scoped, records who made the change in place of the current note.
  const email = useQueryData(USER_QUERY_KEY, userEmailSelector);
  const initialNote = buildDefaultStatusNote(email);
  const summary =
    summaryProp ??
    (count === 1
      ? rows[0]?.fileName || rows[0]?.sha256 || '--'
      : pluralize(count, noun) + ' selected');

  const groupMode = noun !== 'file';
  const fileLabel =
    fileCount != null
      ? `${fileCount.toLocaleString()} ${fileCount === 1 ? 'file' : 'files'}`
      : null;
  const countsKnown = groupMode && !fileCountLoading && fileLabel != null;
  const countsLine = countsKnown
    ? `${fileLabel} across ${pluralize(count, noun)}${
        count === 1 && summary ? ` (${summary})` : ''
      }`
    : null;
  const countsLoading = groupMode && !countsKnown;
  const showSummaryLine = !groupMode && !!summary;
  const submitLabel = !groupMode
    ? 'Save'
    : fileLabel != null
      ? `Update ${fileLabel}`
      : 'Update files';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#3f3f3f' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify icon={UPDATE_TRUST_STATUS_ICON} width={22} height={22} color="#87C8E0" />
        {count > 1
          ? `${UPDATE_TRUST_STATUS_TITLE} (${pluralize(count, noun)})`
          : UPDATE_TRUST_STATUS_TITLE}
        <Tooltip
          arrow
          placement="right"
          componentsProps={{ tooltip: { sx: { maxWidth: 500, pointerEvents: 'auto' } } }}
          title={
            <Typography variant="body2" mb={1}>
              Setting the status to Trusted records that decision in AppControl, but it does not
              change what your devices allow on its own. You still need to update your trusted files
              catalog and deploy it before the trust decision takes effect.{' '}
              <Link
                href={EDIT_STATUS_NOTE_INFO_LINK}
                target="_blank"
                rel="noopener"
                underline="hover">
                More info
              </Link>
            </Typography>
          }>
          <IconButton size="small">
            <Iconify icon="material-symbols:info-outline" width={24} height={24} />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent>
        {showSummaryLine ? (
          <Typography
            variant="body2"
            sx={{ color: '#ccc', mb: countsLine ? 0.5 : 2, wordBreak: 'break-word' }}>
            {summary}
          </Typography>
        ) : null}
        {countsLoading ? (
          <Skeleton variant="rounded" width={180} height={18} sx={{ bgcolor: '#4B4B4B', mb: 2 }} />
        ) : countsLine ? (
          <Typography variant="body2" sx={{ color: '#9CA3AF', mb: 2, fontWeight: 600 }}>
            {countsLine}
          </Typography>
        ) : null}
        {filterScope ? (
          <Alert
            severity="info"
            icon={<Iconify icon="material-symbols:info-outline" width={20} height={20} />}
            sx={{
              alignItems: 'flex-start',
              mb: 2,
              backgroundColor: 'rgba(135, 200, 224, 0.14)',
              border: '1px solid rgba(135, 200, 224, 0.4)',
              borderRadius: '8px',
              color: '#EAF6FB',
              '& .MuiAlert-icon': { color: '#87C8E0' },
            }}>
            <Typography variant="body2">{filterScope.description}</Typography>
            <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
              {filterScope.items.map(item => (
                <Box component="li" key={item.label} sx={{ mb: 0.25 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    <strong>{item.label}:</strong> {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Alert>
        ) : null}

        <Box>
          <UnmatchedFileStatusForm
            loading={loading}
            onCancel={onClose}
            onSubmit={onSave}
            initialStatus={initialStatus}
            initialNote={initialNote}
            submitLabel={submitLabel}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UnmatchedFileStatusModal;
